import {
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import {
  TALK_TO_SALES_AGENDA,
  TALK_TO_SALES_CALENDLY_URL,
  TALK_TO_SALES_STATS,
} from '../../../data/dialog-talk-to-sales';
import { dialogRiseWithLeaveAnimation } from '../../../animations/pricing-animations';
import { PlanTemplatesService } from '../../../services/plan-templates.service';

export interface TalkToSalesData {
  planName?: string;
  calendlyUrl?: string;
}

const CALENDLY_WIDGET_SCRIPT = 'https://assets.calendly.com/assets/external/widget.js';

interface CalendlyInlineWidgetOptions {
  url: string;
  parentElement: HTMLElement;
  resize?: boolean;
}

interface CalendlyGlobal {
  initInlineWidget: (options: CalendlyInlineWidgetOptions) => void;
}

declare global {
  interface Window {
    Calendly?: CalendlyGlobal;
  }
}

@Component({
  selector: 'app-dialog-talk-to-sales',
  imports: [MatIcon],
  templateUrl: './dialog-talk-to-sales.component.html',
  styleUrl: './dialog-talk-to-sales.component.scss',
  animations: [dialogRiseWithLeaveAnimation],
})
export class DialogTalkToSalesComponent implements OnInit, OnDestroy {
  @ViewChild('calendlyHost', { static: true })
  private readonly calendlyHost!: ElementRef<HTMLElement>;

  public readonly data: TalkToSalesData = inject(MAT_DIALOG_DATA, { optional: true }) ?? {};
  public readonly dialogRef = inject(MatDialogRef<DialogTalkToSalesComponent>);
  private readonly _planTemplatesService = inject(PlanTemplatesService);

  readonly enterprisePlan = computed(() =>
    this.planTemplates().find((plan) => plan.type === 'ENTERPRISE') ?? null,
  );
  readonly planTemplates = this._planTemplatesService.planTemplates;

  public readonly calendlyUrl = this.data.calendlyUrl ?? TALK_TO_SALES_CALENDLY_URL;
  public readonly agenda = TALK_TO_SALES_AGENDA;
  public readonly stats = TALK_TO_SALES_STATS;

  ngOnInit(): void {
    this._planTemplatesService.startPlanTemplatesSync();
    this._initCalendlyWidget();
  }

  ngOnDestroy(): void {
    this.calendlyHost.nativeElement.replaceChildren();
  }

  private async _initCalendlyWidget(): Promise<void> {
    await this._loadCalendlyScript();
    window.Calendly?.initInlineWidget({
      url: this.calendlyUrl,
      parentElement: this.calendlyHost.nativeElement,
      resize: true,
    });
  }

  private _loadCalendlyScript(): Promise<void> {
    if (window.Calendly) {
      return Promise.resolve();
    }

    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${CALENDLY_WIDGET_SCRIPT}"]`,
    );
    if (existingScript) {
      return new Promise((resolve) => {
        if (window.Calendly) {
          resolve();
          return;
        }
        existingScript.addEventListener('load', () => resolve(), { once: true });
      });
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = CALENDLY_WIDGET_SCRIPT;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Calendly widget script'));
      document.head.appendChild(script);
    });
  }
}
